import React, { useState } from 'react';
import { Button, Icon, Label, RadioGroup, Text } from 'components';
import { Dialog } from '@blueprintjs/core';
import { useMutation } from '@apollo/client';
import { MIGRATE_ARQAM_USER_MUTATION, USERS_QUERY } from 'gql';
import { useModal } from 'providers/modalProvider';
import { useToast } from 'providers/toastProvider';
import { title } from 'utils/helpers';
import { FormattedMessage, useIntl } from 'react-intl';

// Drop empty/undefined values so optional fields (and Float `power`) aren't sent as "".
const cleanInput = (input = {}) =>
  Object.fromEntries(
    Object.entries(input).filter(
      ([, value]) => value !== '' && value !== undefined && value !== null
    )
  );

const MigrateUserDialog = () => {
  const { modalDispatch, modalState } = useModal();
  const { showToast } = useToast();
  const { formatMessage } = useIntl();
  const {
    conflicts = [],
    input = {},
    summary = {},
    typed = {},
    draft,
    variables,
  } = modalState || {};

  const single = conflicts.length === 1;
  // For a single match we skip straight to the confirm step.
  const [selectedId, setSelectedId] = useState(
    single ? String(conflicts[0]?.id) : undefined
  );
  const [confirmed, setConfirmed] = useState(single);

  const [migrateArqamUserMutation, { loading }] = useMutation(
    MIGRATE_ARQAM_USER_MUTATION,
    {
      onError: ({ message }) => showToast({ message, icon: 'error' }),
      update(dataStore, { data: { migrateArqamUser } }) {
        try {
          const usersData = dataStore.readQuery({
            query: USERS_QUERY,
            variables,
          });
          dataStore.writeQuery({
            query: USERS_QUERY,
            variables,
            data: {
              users: {
                users: [migrateArqamUser, ...usersData.users.users],
                totalCount: usersData.users.totalCount + 1,
              },
            },
          });
        } catch {
          // users list not in cache for these variables — nothing to patch
        }
      },
      onCompleted: () => {
        modalDispatch({ type: 'CLOSE_ALL' });
        showToast({
          message: formatMessage({
            id: 'containers.users.form.successMigrate',
          }),
        });
      },
    }
  );

  if (!conflicts.length) return false;

  const arqamUser = conflicts.find((u) => String(u.id) === selectedId);
  const showConfirm = confirmed && arqamUser;

  const onMigrate = () =>
    migrateArqamUserMutation({
      variables: {
        input: { legacyId: arqamUser.id, ...cleanInput(input) },
      },
    });

  // Reopen the Create form prefilled with what the admin entered, so they can edit it.
  // Strip id/legacyId so the form stays in CREATE mode and submits createUser (the
  // user doesn't exist in Ops yet — this is not an edit-user flow).
  const onEdit = () => {
    const { id, legacyId, ...editableDraft } = draft || {};
    modalDispatch({
      type: 'CREATE_USER',
      payload: { variables, draft: editableDraft },
    });
  };

  // We don't get a per-user matchedBy from the backend, so derive it from what was typed.
  const matchedBy = (u) => {
    const byEmail =
      typed.email && u.email?.toLowerCase() === typed.email.toLowerCase();
    const byHr = typed.hrCode && u.hrcode === typed.hrCode;
    if (byEmail && byHr) {
      return formatMessage({
        id: 'containers.users.modals.migrate.matchedBy.both',
      });
    }
    if (byEmail) {
      return formatMessage({
        id: 'containers.users.modals.migrate.matchedBy.email',
      });
    }
    if (byHr) {
      return formatMessage({
        id: 'containers.users.modals.migrate.matchedBy.hrCode',
      });
    }
    return '';
  };

  const items = conflicts.map((u) => ({
    text: (
      <span className="conflict-option">
        {`${u.first_name} ${u.last_name} — ${u.email} — ${u.hrcode}`}
        {matchedBy(u) && <span className="matched-badge">{matchedBy(u)}</span>}
      </span>
    ),
    value: String(u.id),
  }));

  const detail = (label, value, testid) => (
    <Label
      variant="Baisc"
      labelHeader={label}
      labelText={value || '—'}
      data-testid={`migrate-${testid}`}
    />
  );

  const fullName = arqamUser
    ? `${arqamUser.first_name} ${arqamUser.last_name}`
    : '';

  const migrateTitle = (
    <div className="create-edit-header">
      <Icon
        icon="resolve"
        color="#999999"
        size={14}
        data-testid="migrate-icon"
      />
      <p data-testid="migrate-title">
        <FormattedMessage id="containers.users.modals.migrate.title" />
      </p>
    </div>
  );

  return (
    <Dialog
      isOpen={modalState?.open}
      title={migrateTitle}
      onClose={() => modalDispatch({ type: 'CLOSE_ALL' })}
      canOutsideClickClose={false}
    >
      <div className="bp3-dialog-body migrate-dialog-body">
        {showConfirm ? (
          <>
            <Text variant="body1" data-testid="migrate-description">
              <FormattedMessage
                id={
                  single
                    ? 'containers.users.modals.migrate.content'
                    : 'containers.users.modals.migrate.review'
                }
              />
            </Text>
            <div className="migrate-details">
              {detail(
                formatMessage({ id: 'containers.all.name' }),
                fullName,
                'name'
              )}
              {detail(
                formatMessage({
                  id: 'containers.users.modals.migrate.fields.arqamId',
                }),
                arqamUser.id,
                'arqamId'
              )}
              {detail(
                formatMessage({ id: 'containers.users.form.email' }),
                arqamUser.email,
                'email'
              )}
              {detail(
                formatMessage({ id: 'containers.users.form.hr' }),
                arqamUser.hrcode,
                'hrCode'
              )}
              {detail(
                formatMessage({ id: 'containers.users.header.sport' }),
                title(summary.sport),
                'sport'
              )}
              {detail(
                formatMessage({ id: 'containers.users.form.line' }),
                title(summary.line),
                'line'
              )}
              {detail(
                formatMessage({ id: 'containers.users.form.lineFunction' }),
                title(summary.lineFunction),
                'lineFunction'
              )}
              {detail(
                formatMessage({ id: 'containers.users.jobTitle' }),
                title(summary.jobTitle),
                'jobTitle'
              )}
              {detail(
                formatMessage({ id: 'containers.users.form.jobType' }),
                title(summary.jobType),
                'jobType'
              )}
            </div>
          </>
        ) : (
          <>
            <Text variant="body1" data-testid="conflict-description">
              <FormattedMessage id="containers.users.modals.conflict.content" />
            </Text>
            <RadioGroup
              className="mgt-20"
              name="arqam-conflict"
              items={items}
              selectedValue={selectedId}
              onRadioChange={(value) => setSelectedId(value)}
            />
          </>
        )}
      </div>
      <div className="bp3-dialog-footer-actions">
        <Button
          outlined
          text={formatMessage({ id: 'containers.users.modals.migrate.edit' })}
          intent="Primary"
          onClick={onEdit}
          disabled={loading}
          data-testid="migrate-edit"
        />
        {showConfirm && !single && (
          <Button
            outlined
            text={formatMessage({ id: 'containers.users.modals.migrate.back' })}
            intent="Primary"
            onClick={() => setConfirmed(false)}
            disabled={loading}
            data-testid="migrate-back"
          />
        )}
        {showConfirm ? (
          <Button
            intent="Primary"
            text={formatMessage({
              id: 'containers.users.modals.migrate.confirm',
            })}
            onClick={onMigrate}
            loading={loading}
            data-testid="migrate-confirm"
          />
        ) : (
          <Button
            intent="Primary"
            text={formatMessage({
              id: 'containers.users.modals.conflict.continue',
            })}
            onClick={() => setConfirmed(true)}
            disabled={!selectedId}
            data-testid="conflict-continue"
          />
        )}
      </div>
    </Dialog>
  );
};

export default MigrateUserDialog;
